<template>
  <div class="flex w-full flex-col gap-6">
    <CompanyNavigation v-if="route.params.companyUuid" :company-uuid="route.params.companyUuid" :company-name="store.options.company?.name" />
    <header class="flex flex-col gap-3 border-b pb-6">
      <h1 class="text-3xl font-semibold tracking-tight">
        Support requests
      </h1>
      <p class="text-sm text-muted-foreground">
        {{ store.options.company ? `Support for ${store.options.company.name}` : 'Review your requests and replies.' }}
      </p>
      <Button class="w-fit" :disabled="store.isLoading || (!!route.params.companyUuid && !store.options.company)" @click="openCreateModal">
        Open a support request
      </Button>
    </header>

    <Alert v-if="store.error" variant="destructive" role="alert">
      <AlertTitle>Support request unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>

    <SupportRequestCreateModal ref="createModal" :company="createCompany" @created="showRequest" />

    <div class="flex items-center gap-3">
      <label for="support-status-filter" class="text-sm font-medium">Status</label>
      <select id="support-status-filter" v-model="status" class="h-10 rounded-md border bg-background px-3 text-sm" :disabled="store.isLoading" @change="loadPage(1)">
        <option value="">
          All statuses
        </option>
        <option v-for="value in store.options.statuses" :key="value" :value="value">
          {{ statusLabel(value) }}
        </option>
      </select>
    </div>
    <p v-if="store.isLoading" role="status" class="text-sm text-muted-foreground">
      Loading support requests…
    </p>
    <div v-else-if="store.items.length" class="flex flex-col gap-3">
      <RouterLink v-for="item in store.items" :key="item.id" :to="{ name: 'SupportRequestPage', params: { supportRequestId: item.id } }" class="flex flex-col gap-2 rounded-xl border bg-card p-5 transition hover:border-primary focus-visible:outline-2 focus-visible:outline-primary">
        <div class="flex items-start justify-between gap-3">
          <h2 class="font-semibold">
            #{{ item.id }} · {{ item.subject }}
          </h2>
          <span class="rounded-md bg-muted px-2 py-1 text-xs">{{ statusLabel(item.status) }}</span>
        </div>
        <p class="text-sm text-muted-foreground">
          {{ item.company?.name ?? 'No company' }} · {{ item.category_label }} · {{ item.user.name }}
        </p>
        <p class="text-xs text-muted-foreground">
          Updated {{ formatDate(item.updated_at) }}
        </p>
      </RouterLink>
    </div>
    <p v-else-if="!store.error" class="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
      No support requests found.
    </p>

    <nav v-if="!store.isLoading && store.pagination.lastPage > 1" class="flex items-center justify-center gap-3" aria-label="Support request pages">
      <Button variant="outline" :disabled="store.pagination.currentPage === 1" @click="loadPage(store.pagination.currentPage - 1)">
        Previous
      </Button>
      <span class="text-sm text-muted-foreground">Page {{ store.pagination.currentPage }} of {{ store.pagination.lastPage }}</span>
      <Button variant="outline" :disabled="store.pagination.currentPage === store.pagination.lastPage" @click="loadPage(store.pagination.currentPage + 1)">
        Next
      </Button>
    </nav>
  </div>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import SupportRequestCreateModal from '../components/SupportRequestCreateModal.vue'
import CompanyNavigation from '../components/CompanyNavigation.vue'
import { useSupportRequestStore } from '../stores/supportRequestStore.js'

const route = useRoute()
const router = useRouter()
const store = useSupportRequestStore()
const createModal = ref(null)
const createCompany = ref(null)
const status = ref('')
const statusLabel = value => ({ new: 'New', in_progress: 'In progress', closed: 'Closed' })[value] ?? value
const formatDate = value => new Date(value).toLocaleString()
const loadPage = page => store.fetchList({ companyUuid: route.params.companyUuid, status: status.value, page })

const openCreateModal = async () => {
  const companyUuid = route.params.companyUuid
  if (store.isLoading || (companyUuid && store.options.company?.uuid !== companyUuid)) {
    return
  }

  createCompany.value = companyUuid ? store.options.company : null
  await nextTick()
  if (route.params.companyUuid === companyUuid) {
    createModal.value?.openModal()
  }
}

const showRequest = created => router.push({ name: 'SupportRequestPage', params: { supportRequestId: created.id } })

watch(() => route.params.companyUuid, () => {
  status.value = ''
  createModal.value?.closeModal()
  loadPage(1)
}, { immediate: true })
</script>
